/**
 * Open a course by its 6-character code (15 Oct 2026). If that course — any of its language
 * versions — has been launched to you in the current organisation, the code opens it as YOUR
 * course (/learn/:launchId), so progress, the quiz and the due date count; otherwise it opens the
 * public player (/play/CODE) as before. Every course-code box in the app shell goes through this.
 *
 * Asked fresh on submit rather than from a cached list: a code is typed rarely, and a launch made
 * a minute ago must be found.
 */
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrgs } from '@/lib/useOrgs'
import { orgApi, type LearningItem } from '@/lib/orgApi'

/** A course code as the product issues them (src/lib/publish.ts): 6 of 23456789ABCDEFGHJKLMNPQRSTUVWXYZ,
 *  with at least one digit so an ordinary six-letter word still searches. */
export const isCourseCode = (s: string) => /^(?=.*[2-9])[2-9A-HJ-NP-Z]{6}$/i.test(s.trim())

/** The launch whose course carries this code, if any (pure — tested). */
export function launchForCode(items: LearningItem[], code: string): LearningItem | null {
  const c = code.trim().toUpperCase()
  return items.find(i => i.versions.some(v => v.shareCode?.toUpperCase() === c)) ?? null
}

export function useOpenCourseCode(backTo?: string): { open: (code: string) => void; busy: boolean } {
  const navigate = useNavigate()
  const orgs = useOrgs()
  const orgId = orgs.status === 'ready' && orgs.current ? orgs.current.id : null
  const [busy, setBusy] = useState(false)
  const open = useCallback((raw: string) => {
    const code = raw.trim().toUpperCase()
    if (code.length !== 6 || busy) return
    if (!orgId) { navigate(`/play/${code}`); return }
    setBusy(true)
    orgApi.myLearning(orgId)
      .then(r => {
        const hit = launchForCode(r.items, code)
        navigate(hit ? `/learn/${hit.launchId}` : `/play/${code}`, hit && backTo ? { state: { backTo } } : undefined)
      })
      .catch(() => navigate(`/play/${code}`))   // the code still works, just not as your course
      .finally(() => setBusy(false))
  }, [orgId, busy, navigate, backTo])
  return { open, busy }
}
